import React from 'react';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link, Typography } from '@mui/material';
import { tourDetailSelectors } from '@lialo/core/lib/pages/tourDetail';
import { RootState } from '@lialo/core/lib/store/root';
import { chooseTourImageLink } from '@lialo/core/lib/utils/helper/image-link';
import { PaymentCheckoutCouponPage, PurchaseCouponInfo } from '@lialo/payment/lib/checkout/coupon';
import routes from '@lialo/core/lib/pages/utils/routes';
import { addYears } from '@lialo/common/lib/date';

import { AppBar } from '@lialo/pwa-ui/lib/PageComponents/AppBar';
import { Page } from '@lialo/pwa-ui/lib/PageComponents/Page';
import { PaymentConfigProvider } from '../../../../app/PaymentConfigProvider';
import RouterBackButton from '../../../../common/Page/AppBarButtons/RouterBackButton';
import { useServiceProvider } from '../../../../common/Providers';
import { PurchaseCouponSummary } from '../PurchaseCouponSummary';
import config from "../../../../config";

const StyledDiv = styled('div')(({ theme }) => ({
  overflowY: 'auto',
  flexGrow: 1,
  // scrollBehavior: 'smooth', // disabled, because scrollIntoView() doesn't work otherwise
  padding: theme.spacing(2),
}));

interface IProps {
  tourCode: string;
  onSuccess: (response: PurchaseCouponInfo) => void;
}

export const PurchasePage = ({ tourCode, onSuccess }: IProps) => {
  const { t, i18n } = useTranslation();

  const { navigationService, analyticsService } = useServiceProvider();

  const tour = useSelector(tourDetailSelectors.selectTourDetails);
  return (
    <Page>
      <AppBar
        title={t('tour_detail.purchase_voucher.title')}
        leftElement={<RouterBackButton parentUrl={navigationService.generateTourDetailUrl(tourCode)} />}
      />
      <StyledDiv>
        {tour && tour.title && tour.price && (
          <>
            <PurchaseCouponSummary
              imageSrc={chooseTourImageLink(tour.mainImages[0]?.url, 500)}
              title={tour.title}
              price={tour.price}
              averageRating={tour.rating}
            />
            <Typography>
              {t('tour_detail.purchase_voucher.coupon_example_1')}
              <Link
                rel="nofollow"
                href={routes.generateVoucherLink(
                  config.voucherGeneratorUrl!,
                  i18n.language,
                  tourCode,
                  '00000000',
                  addYears(new Date(), 3)
                )}
                target={'_blank'}
                download={`example-voucher-${tourCode}.pdf`}
              >
                {t('tour_detail.purchase_voucher.coupon_example_2')}
              </Link>
              {t('tour_detail.purchase_voucher.coupon_example_3')}
            </Typography>
            <PaymentConfigProvider>
              <PaymentCheckoutCouponPage
                shortLink={tour.shortLink}
                productName={tour.title}
                price={tour.price}
                onPurchase={onSuccess}
                onPaymentMethodSelected={type =>
                  analyticsService.logPaymentMethodSelected(
                    tourCode,
                    type,
                    tour.price,
                    tour.user?.id,
                    tour.startLocation?.city
                  )
                }
              />
            </PaymentConfigProvider>
          </>
        )}
      </StyledDiv>
    </Page>
  );
};
